import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  IndianRupee, Bookmark, BookmarkCheck, Share2, ExternalLink,
  ArrowUp, Building2, MapPin, Briefcase, Users,
} from "lucide-react";

/**
 * StickyApplyPanel — right-rail sticky panel on job detail (AmbitionBox pattern).
 * On mobile, transforms into a bottom-fixed action bar with the primary CTA.
 *
 * Props:
 *   salary           — string ("₹8–14 LPA")   optional
 *   location, exp    — strings                optional
 *   openings         — number                 optional
 *   posted           — string ("2d ago")      optional
 *   applied, applying — booleans
 *   isStudent        — logged-in student → clicks call onApply, otherwise Login redirect
 *   onApply          — () => void            student-only apply handler
 *   onSave           — () => void            optional
 *   saved            — boolean
 *   onShare          — () => void
 *   copied           — boolean               feedback after share
 *   external         — boolean               job is external
 *   externalUrl      — string                external apply URL
 *   toc              — [{ id, label }]        table-of-contents anchor links
 *   loginTo          — string                 "/login" or "/signup"
 *   loginState       — object                 router state for next-url
 */
export default function StickyApplyPanel({
  salary, location, exp, openings, posted,
  applied, applying, isStudent,
  onApply, onSave, saved, onShare, copied,
  external, externalUrl,
  toc = [],
  loginTo = "/login", loginState,
}) {
  const [activeAnchor, setActiveAnchor] = useState(toc[0]?.id || null);

  // Track scroll to highlight the active TOC anchor
  useEffect(() => {
    if (!toc.length) return undefined;
    const anchors = toc
      .map((t) => document.getElementById(t.id))
      .filter(Boolean);
    if (!anchors.length) return undefined;

    const obs = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
        if (visible?.target?.id) setActiveAnchor(visible.target.id);
      },
      { rootMargin: "-40% 0px -55% 0px", threshold: 0 },
    );
    anchors.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, [toc]);

  const ApplyBtn = ({ full = false, size = "md" }) => {
    const cls = `${size === "sm" ? "text-sm py-2 px-4" : "text-sm py-3 px-5"} ${full ? "w-full" : ""} inline-flex items-center justify-center gap-1.5 font-semibold rounded-md text-white shadow-sm hover:shadow-md transition disabled:opacity-60 disabled:cursor-not-allowed`;
    const style = { background: "linear-gradient(135deg, #006CFF 0%, #0040A8 100%)" };
    if (external) {
      return (
        <a
          href={externalUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={cls}
          style={style}
          data-testid="apply-panel-external-cta"
        >
          Apply on external site <ExternalLink size={14} />
        </a>
      );
    }
    if (isStudent) {
      return (
        <button
          type="button"
          onClick={onApply}
          disabled={applied || applying}
          className={cls}
          style={style}
          data-testid="apply-panel-cta"
        >
          {applied ? "Applied ✓" : applying ? "Applying…" : "Apply now"}
        </button>
      );
    }
    return (
      <Link
        to={loginTo}
        state={loginState}
        className={cls}
        style={style}
        data-testid="apply-panel-cta"
      >
        Log in to apply
      </Link>
    );
  };

  return (
    <>
      {/* Desktop panel — sticky right rail */}
      <aside
        className="hidden lg:block sticky top-24 space-y-3"
        data-testid="sticky-apply-panel"
      >
        {/* Primary card */}
        <div className="bg-white border border-rule rounded-xl p-5 shadow-sm">
          {salary && (
            <div className="flex items-baseline gap-1 mb-3">
              <IndianRupee size={16} className="text-signal" />
              <span className="text-lg font-bold text-slate-900">{salary}</span>
            </div>
          )}
          <ul className="space-y-2 text-sm mb-4">
            {location && (
              <li className="flex items-center gap-2 text-slate-600">
                <MapPin size={14} className="text-muted shrink-0" />
                <span className="truncate">{location}</span>
              </li>
            )}
            {exp && (
              <li className="flex items-center gap-2 text-slate-600">
                <Briefcase size={14} className="text-muted shrink-0" />
                {exp}
              </li>
            )}
            {openings && (
              <li className="flex items-center gap-2 text-slate-600">
                <Users size={14} className="text-muted shrink-0" />
                {openings} opening{openings > 1 ? "s" : ""}
              </li>
            )}
            {posted && (
              <li className="text-xs text-muted pt-1">Posted {posted}</li>
            )}
          </ul>

          <ApplyBtn full />

          <div className="mt-3 flex gap-2">
            {onSave && (
              <button
                type="button"
                onClick={onSave}
                className="flex-1 inline-flex items-center justify-center gap-1.5 text-sm font-semibold text-slate-700 border border-rule rounded-md py-2 hover:bg-slate-50 transition"
                data-testid="apply-panel-save"
              >
                {saved ? <BookmarkCheck size={14} className="text-signal" /> : <Bookmark size={14} />}
                {saved ? "Saved" : "Save"}
              </button>
            )}
            <button
              type="button"
              onClick={onShare}
              className="flex-1 inline-flex items-center justify-center gap-1.5 text-sm font-semibold text-slate-700 border border-rule rounded-md py-2 hover:bg-slate-50 transition"
              data-testid="apply-panel-share"
            >
              <Share2 size={14} />
              {copied ? "Copied" : "Share"}
            </button>
          </div>
        </div>

        {/* Table of contents */}
        {toc.length > 0 && (
          <nav
            className="bg-white border border-rule rounded-xl p-4"
            aria-label="On this page"
            data-testid="apply-panel-toc"
          >
            <div className="text-[10px] font-bold uppercase tracking-wider text-muted mb-2">
              On this page
            </div>
            <ul className="space-y-1">
              {toc.map((t) => (
                <li key={t.id}>
                  <a
                    href={`#${t.id}`}
                    className={`block text-sm py-1 pl-2 border-l-2 transition ${activeAnchor === t.id ? "border-signal text-signal font-semibold" : "border-transparent text-slate-600 hover:text-signal hover:border-signal/40"}`}
                    data-testid={`apply-panel-toc-${t.id}`}
                  >
                    {t.label}
                  </a>
                </li>
              ))}
            </ul>
            <button
              type="button"
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              className="mt-3 pt-3 border-t border-rule w-full inline-flex items-center justify-center gap-1 text-xs text-muted hover:text-signal transition"
              aria-label="Back to top"
            >
              <ArrowUp size={12} /> Back to top
            </button>
          </nav>
        )}

        {/* Company mini-card fallback (only if student is not logged in) */}
        {!isStudent && !external && (
          <div className="bg-slate-50 border border-rule rounded-xl p-4 text-sm">
            <div className="font-semibold flex items-center gap-1.5">
              <Building2 size={14} className="text-signal" />
              New here?
            </div>
            <p className="text-xs text-muted mt-1.5 mb-3">
              One SkillConnect profile applies to every employer.
            </p>
            <Link
              to="/signup"
              state={loginState}
              className="block text-center text-sm font-semibold text-signal border border-signal/40 rounded-md py-2 hover:bg-signal hover:text-white transition"
              data-testid="apply-panel-signup"
            >
              Create student account
            </Link>
          </div>
        )}
      </aside>

      {/* Mobile / tablet — bottom-fixed apply bar */}
      <div
        className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-rule px-4 py-3 flex items-center gap-3 shadow-[0_-4px_16px_-8px_rgba(0,0,0,0.15)]"
        data-testid="mobile-apply-bar"
      >
        {salary && (
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-wider text-muted font-semibold">Salary</div>
            <div className="text-sm font-bold text-slate-900 truncate">{salary}</div>
          </div>
        )}
        <ApplyBtn size="sm" />
      </div>
    </>
  );
}
